import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Dialog,
  Grid,
  IconButton,
  SxProps,
  Theme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useDispatch } from 'react-redux';
import { TP_COLORS } from '../../../../constants';
import { ISubTopic, ISubTopicVideoLink } from '../../../../store/SubTopicSlice';
import { getThemeMode } from '../../../../store/ConfigSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { saveUserUsage } from '../../../../store/AccessLimitSlice/AccessLimitApi';
import {
  getAccessLimitState,
  toggleLeadGenModal,
  togglePremiumModal,
} from '../../../../store/AccessLimitSlice';
import { useAppSelector } from '../../../../hooks';
import {
  getYouTubeEmbedUrlByLink,
  getYouTubeVideoIdFromUrl,
  getYoutubeVideoImageThumbnailByVideoId,
} from '../../../../helper';

interface WatchVideosModalProps {
  open: boolean;
  onClose: () => void;
  subTopic: ISubTopic;
}

const previewVideoContainerStyles: SxProps<Theme> = {
  border: 'none',
  borderRadius: '12px',
  width: '100%',
  minHeight: { xs: '300px', sm: '400px', md: '400px', lg: '500px' },
};

const thumbContainerStyles: SxProps<Theme> = {
  backgroundColor: TP_COLORS.black,
  borderRadius: '12px',
  padding: '15px',
  marginBottom: '15px',
};

const WatchVideosModal: React.FC<WatchVideosModalProps> = ({
  open,
  onClose,
  subTopic,
}) => {
  const dispatch = useDispatch();

  // Store
  const themeMode = useAppSelector(getThemeMode);
  const authState = useAppSelector(getAuthState);
  const accessLimitState = useAppSelector(getAccessLimitState);

  const { usage, limitation } = accessLimitState;
  const authUser = authState.user;

  // State
  const [selectedVideo, setSelectedVideo] = useState<
    ISubTopicVideoLink | undefined
  >(undefined);

  const videos = subTopic.videoLinks || [];

  const isPremiumModal =
    authUser &&
    (authUser.role.name === 'Student' || authUser.isLeadGenSubmitted);

  if (themeMode === 'dark') {
    thumbContainerStyles.backgroundColor = 'background.paper';
  }

  useEffect(() => {
    if (videos.length) {
      setSelectedVideo(videos[0]);
    }
  }, []);

  useEffect(() => {
    if (
      selectedVideo &&
      authUser &&
      (authUser.role.name === 'Guest' || authUser.role.name === 'Student')
    ) {
      dispatch(
        saveUserUsage({
          userId: authUser._id,
          option: 'videoWatches',
        })
      );
    }
  }, [selectedVideo]);

  const handleVideoChange = (video: ISubTopicVideoLink) => {
    if (
      limitation &&
      limitation.videoWatches &&
      limitation.videoWatches > 0 &&
      usage.videoWatches >= limitation.videoWatches
    ) {
      dispatch(isPremiumModal ? togglePremiumModal() : toggleLeadGenModal());
    } else {
      setSelectedVideo(video);
    }
  };

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={onClose}
      TransitionProps={{ style: { margin: 0, overflow: 'auto' } }}
      PaperProps={{
        elevation: 0,
        style: {
          backgroundColor: 'transparent',
          maxWidth: 'unset',
          overflow: 'unset',
        },
      }}
    >
      <Container role="presentation" maxWidth="md" style={{ padding: 0 }}>
        <div style={{ position: 'fixed', right: '15px', top: '15px' }}>
          <IconButton size="large" onClick={onClose}>
            <CloseIcon
              style={{ color: TP_COLORS.white, width: '40px', height: '40px' }}
            />
          </IconButton>
        </div>
        <div style={{ marginBottom: '30px' }}>
          {selectedVideo ? (
            <Box
              component="iframe"
              loading="eager"
              allowFullScreen
              title={selectedVideo.title}
              src={getYouTubeEmbedUrlByLink(selectedVideo.link)}
              sx={previewVideoContainerStyles}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            />
          ) : null}
        </div>
        <Box sx={thumbContainerStyles}>
          <Grid container spacing={2}>
            {videos.map((video, index) => {
              const isSelected = selectedVideo?._id === video._id;
              const videoId = getYouTubeVideoIdFromUrl(video.link);
              const thumb = getYoutubeVideoImageThumbnailByVideoId(
                videoId,
                'mqdefault'
              );

              const cardStyles: SxProps<Theme> = {
                padding: 0,
                borderRadius: '7px',
              };

              return (
                <Grid key={index} item xs={6} sm={3} md={3} lg={2}>
                  <Card sx={cardStyles}>
                    <CardActionArea onClick={() => handleVideoChange(video)}>
                      <CardContent style={{ padding: 0 }}>
                        <img src={thumb} alt={video.title} width="100%" />
                      </CardContent>
                    </CardActionArea>
                  </Card>
                  <div style={{ padding: '0 20%' }}>
                    <Box
                      sx={{
                        marginTop: '6px',
                        width: '100%',
                        minHeight: '3px',
                        borderRadius: '4px',
                        backgroundColor: isSelected
                          ? 'primary.main'
                          : undefined,
                      }}
                    />
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Container>
    </Dialog>
  );
};

export default WatchVideosModal;
